import React from "react";
import { Loader2, Sparkles, ShieldCheck, BookOpen, Paperclip, Globe, TrendingUp } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { LiveActionStatus } from "./LiveActionStatus";

/* -------------------------------- Markdown ------------------------------- */
export const MD_COMPONENTS = {
  table: ({ node, ...props }) => (
    <div className="my-3 -mx-1 overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm border-collapse" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-subtle text-text-secondary" {...props} />,
  th: ({ node, ...props }) => (
    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider border-b border-border" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="px-3 py-2 border-b border-border last:border-0 text-text-primary" {...props} />
  ),
  tr: ({ node, ...props }) => <tr className="hover:bg-subtle/50" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2 space-y-1" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2 space-y-1" {...props} />,
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  h1: ({ node, ...props }) => <h3 className="text-base font-semibold text-text-primary mt-3 mb-1.5" {...props} />,
  h2: ({ node, ...props }) => <h3 className="text-base font-semibold text-text-primary mt-3 mb-1.5" {...props} />,
  h3: ({ node, ...props }) => <h4 className="text-sm font-semibold text-text-primary mt-2.5 mb-1" {...props} />,
  h4: ({ node, ...props }) => <h5 className="text-sm font-semibold text-text-primary mt-2 mb-1" {...props} />,
  p: ({ node, ...props }) => <p className="my-2 leading-relaxed" {...props} />,
  a: ({ node, ...props }) => (
    <a target="_blank" rel="noopener noreferrer"
       className="text-accent-strong underline decoration-accent/40 hover:decoration-accent transition-all" {...props} />
  ),
  code: ({ node, inline, ...props }) =>
    inline ? (
      <code className="px-1.5 py-0.5 rounded bg-subtle text-text-primary text-[12px] font-mono" {...props} />
    ) : (
      <code className="block p-3 rounded-md bg-subtle text-text-primary text-[12px] font-mono overflow-x-auto" {...props} />
    ),
  pre: ({ node, ...props }) => <pre className="my-2" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-2 border-accent pl-3 my-2 text-text-secondary italic" {...props} />
  ),
  strong: ({ node, ...props }) => <strong className="font-semibold text-text-primary" {...props} />,
  hr: ({ node, ...props }) => <hr className="my-3 border-border" {...props} />,
};

export const MarkdownView = ({ text }) => (
  <div className="text-sm leading-relaxed text-text-primary [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{text}</ReactMarkdown>
  </div>
);

/* --------------------------------- Tool Badges --------------------------- */
export const TOOL_BADGES = {
  "market:prices": { label: "Prezzi GME", title: "Dati reali dal Gestore dei Mercati Energetici" },
  "market:psv": { label: "PSV (GME)", title: "Indice IGI del GME — prezzo gas al PSV" },
  "web:search": { label: "Ricerca web", title: "Risposta basata su ricerca web (vedi Fonti)" },
};

export const ToolBadges = ({ tools }) => {
  if (!tools || tools.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="tool-badges">
      {tools.map((t) => {
        const meta = TOOL_BADGES[t];
        if (!meta) return null;
        return (
          <span
            key={t}
            title={meta.title}
            data-testid={`tool-badge-${t}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success-bg/60 border border-success/30 text-[10px] text-success-strong font-medium tracking-wide uppercase"
          >
            <ShieldCheck size={10} strokeWidth={2.5} />
            {meta.label}
          </span>
        );
      })}
    </div>
  );
};

/* --------------------------------- Message ------------------------------- */
export const ChatMessage = ({ msg }) => {
  if (msg.role === "system_meta") {
    return (
      <div className="flex justify-center my-6" data-testid="msg-welcome">
        <div className="max-w-2xl bg-subtle border border-border rounded-xl px-5 py-4 text-sm text-text-secondary leading-relaxed">
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkles size={14} className="text-accent" />
            <span className="text-xs font-medium uppercase tracking-wider text-text-muted">AI aziendale</span>
          </div>
          {msg.content}
        </div>
      </div>
    );
  }
  const isUser = msg.role === "user";
  const MODEL_LABELS = { claude: "Claude Sonnet 4.6", gemini: "Gemini 3 Flash" };
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} my-3`} data-testid={`msg-${msg.role}`}>
      <div className="flex flex-col max-w-3xl">
        <div className={`rounded-2xl px-5 py-3.5 ${
          isUser
            ? "bg-brand text-white text-sm leading-relaxed whitespace-pre-wrap"
            : "bg-surface border border-border"
        }`}>
          {isUser ? msg.content : (
            <>
              <MarkdownView text={msg.content} />
              {msg.streaming && msg.tool_status && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-text-muted" data-testid="msg-tool-status">
                  <Loader2 size={11} className="animate-spin" />
                  {msg.tool_status}
                </div>
              )}
              {msg.streaming && (
                <span className="inline-block w-2 h-4 ml-0.5 align-text-bottom animate-pulse rounded-sm"
                      style={{ background: "var(--wl-brand-yellow, #F4F430)" }} aria-hidden="true" />
              )}
            </>
          )}
          {!isUser && msg.action_meta?.executed_request_id && (
            <LiveActionStatus meta={msg.action_meta} />
          )}
        </div>
        {isUser && (msg.attachments || []).length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5 justify-end" data-testid="msg-attachments">
            {msg.attachments.map((a) => (
              <span key={a.id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-subtle border border-border text-[10px] text-text-secondary max-w-[220px]"
                    data-testid={`msg-attachment-${a.id}`}>
                <Paperclip size={9} className="flex-shrink-0" />
                <span className="truncate">{a.filename}</span>
              </span>
            ))}
          </div>
        )}
        {!isUser && !msg.streaming && msg.model_key && (
          <div className="mt-1 text-[10px] text-text-muted pl-1" data-testid="msg-model">
            {MODEL_LABELS[msg.model_key] || msg.model_key}
          </div>
        )}
        {!isUser && <ToolBadges tools={msg.tools_used} />}
        {!isUser && (msg.market_boxes || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2" data-testid="market-boxes">
            {msg.market_boxes.map((b, i) => (
              <div key={`${b.metric}-${i}`} data-testid={`market-box-${i}`}
                   className="rounded-xl border border-border bg-surface px-4 py-3 min-w-[190px] shadow-sm">
                <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-text-muted">
                  <TrendingUp size={11} className="text-accent-strong" /> {b.metric}
                </div>
                <div className="text-lg font-semibold text-text-primary mt-0.5" data-testid={`market-box-value-${i}`}>
                  {b.value}
                </div>
                <div className="text-[10px] text-text-muted mt-1">
                  dati del {b.date} ·{" "}
                  {b.source_url ? (
                    <a href={b.source_url} target="_blank" rel="noopener noreferrer"
                       className="underline decoration-border underline-offset-2 hover:text-accent-strong">
                      {b.source}
                    </a>
                  ) : b.source}
                </div>
                <div className="text-[10px] text-text-muted">aggiornato alle {b.updated_at}</div>
              </div>
            ))}
          </div>
        )}
        {!isUser && (msg.web_sources || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="web-sources">
            {msg.web_sources.map((s, i) => (
              <a
                key={`${s.url}-${i}`}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                title={s.url}
                data-testid={`web-source-${i}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-subtle border border-border text-[10px] text-text-secondary font-medium hover:border-accent hover:text-accent-strong transition-colors max-w-[280px]"
              >
                <Globe size={10} strokeWidth={2.5} className="flex-shrink-0" />
                <span className="truncate">{s.title}</span>
              </a>
            ))}
          </div>
        )}
        {!isUser && (msg.kb_sources || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="kb-sources">
            {msg.kb_sources.map((s) => (
              <span
                key={s.doc_id}
                title="Risposta basata sulla base documentale aziendale"
                data-testid={`kb-source-${s.doc_id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-subtle border border-accent/30 text-[10px] text-accent-strong font-medium"
              >
                <BookOpen size={10} strokeWidth={2.5} />
                Fonte: {s.title}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const TypingIndicator = () => (
  <div className="flex justify-start my-3" data-testid="typing-indicator">
    <div className="bg-surface border border-border rounded-2xl px-5 py-3.5 flex items-center gap-2 text-sm text-text-secondary">
      <Loader2 size={14} className="animate-spin" />
      <span>L&apos;assistente sta scrivendo…</span>
    </div>
  </div>
);

export const WELCOME_MESSAGE = {
  role: "system_meta",
  content:
    "Sono l'AI aziendale di Wattline: chiedi quello che ti serve senza uscire dalla piattaforma — i tuoi dati restano qui. " +
    "Posso scrivere email, PEC e comunicazioni, riassumere e spiegare testi, rispondere sulle procedure interne " +
    "caricate dalla tua azienda (citando la fonte) e aiutarti a impostare calcoli.",
};
